/*
  Copyright Epic Games, Inc. All Rights Reserved.
*/

import { CSSProperties, useMemo } from "react";
import { css, cx } from "@panda/css";
import { ColorToken, token } from "@panda/tokens";
import { useHeadingLevelOffset } from "@components/context/HeadingLevelContext";

import { CosmosTextAs, CosmosTextVariantEnum, CosmosVariableRichText } from "../text";
import { useFormatHtml } from "./CosmosRichText.utils";
import { getTextAs, getTextVariantType, shiftHeadingTag } from "../text/text.utils";

export interface CosmosRichTextProps extends Omit<CosmosVariableRichText, "text" | "as"> {
	children?: React.ReactNode;
	className?: string;
	isGameMode?: boolean;
	color?: ColorToken;
	style?: CSSProperties;
	as?: CosmosTextAs | null;
}

interface CssVariables extends CSSProperties {
	"--color"?: string;
}

const styles = css({
	color: "var(--color)",
	// Game mode replaces non-allowlisted anchors with this span (see
	// CosmosRichText.game.ts / stripAnchors). An explicit colour, NOT opacity: the
	// old inline `opacity: 0.65` multiplied with text already at
	// eds.text.secondary (itself white at 0.65), compositing to #757577 on the
	// #101014 background — 4.13:1, under WCAG AA's 4.5:1 for 16px body copy. It
	// also kept an inline style in a library that is Panda-only.
	"& .cosmos-neutralised-link": {
		color: "eds.text.secondary",
	},
	"& a[href]": {
		color: "eds.fill.primary.default",
		textDecoration: "underline",
		transition: `color token(animations.eds.duration.200) token(animations.eds.easing.easeInOutQuad)`,
		_hover: {
			color: "eds.fill.primary.hover",
		},
	},
	"& ol, & ul": {
		listStyleType: "revert",
		marginBlock: "eds.dimension.16",
		paddingInlineStart: "eds.dimension.40",
		"& li": {
			marginBlockEnd: "eds.dimension.12",
			"&:last-child": {
				marginBlockEnd: "0",
			},
		},
	},
	"& p": {
		marginBlock: "eds.dimension.16",
	},
	"& p:first-child": {
		marginBlockStart: "0",
	},
	"& p:last-child": {
		marginBlockEnd: "0",
	},
	"& blockquote": {
		borderInlineStartWidth: "4px",
		borderInlineStartStyle: "solid",
		borderInlineStartColor: "eds.fill.primary.default",
		paddingInlineStart: "eds.dimension.32",
		paddingBlock: "eds.dimension.8",
	},
	"& i, & em": {
		fontStyle: "italic",
	},
	"& code": {
		fontFamily: "revert",
	},
	"& hr": {
		marginBlock: "eds.dimension.32",
		width: "100%",
		borderBlockStartWidth: "2px",
		borderBlockStartStyle: "solid",
		borderBlockStartColor: "eds.border.subtle",
	},
	"& table": {
		maxWidth: "100%",
	},
	"& table th": {
		textStyle: "uiMD",
		color: "eds.text.secondary",
		paddingBlock: "eds.dimension.8",
		paddingInline: "eds.dimension.16",
		textAlign: "start",
	},
	"& table td": {
		textStyle: "uiMD",
		color: "eds.text.primary",
		padding: "eds.dimension.16",
		background: "eds.background.elevated.low.transparent",
		borderBottom: "2px solid",
		borderBottomColor: "eds.background.default",
	},
	"& table tr:first-child td:first-child": {
		borderTopLeftRadius: "eds.borderRadius.12",
	},
	"& table tr:first-child td:last-child": {
		borderTopRightRadius: "eds.borderRadius.12",
	},
	"& table tr:last-child td:first-child": {
		borderBottomLeftRadius: "eds.borderRadius.12",
	},
	"& table tr:last-child td:last-child": {
		borderBottomRightRadius: "eds.borderRadius.12",
	},
});

export const CosmosRichText = (props: CosmosRichTextProps): React.JSX.Element | null => {
	const __html = useFormatHtml(props.html, { gameMode: props.isGameMode });
	const headingOffset = useHeadingLevelOffset();

	const style: CssVariables = useMemo(() => {
		if (props.color) {
			return { ...props.style, "--color": token(`colors.${props.color}`) };
		}
		return props.style || {};
	}, [props.color, props.style]);

	// React 19's commitHostUpdate compares each prop with `!==`, and for
	// dangerouslySetInnerHTML that prop is this wrapper object — it no longer
	// reaches inside for `__html` the way React 18's diffProperties did. A fresh
	// object literal per render therefore fails the comparison every time, and
	// React re-assigns innerHTML, tearing down and re-parsing the whole subtree
	// with byte-identical content. On a legal page that is the entire body: 473
	// nodes rebuilt the moment anything above re-renders, i.e. the MW-13210 flash.
	const innerHtml = useMemo(() => ({ __html }), [__html]);

	if (!props.html) {
		return null;
	}

	const as = shiftHeadingTag(getTextAs(props), headingOffset);
	const Component = as && !props.html.startsWith("<h1>") ? as : "span";

	// Don't emit an empty heading: a heading whose content is only empty/whitespace
	// markup (e.g. "<p></p>", "&nbsp;", "<br/>") is an accessibility defect
	// (EMPTY_HEADING). Media-only headings (an <img>/<svg> with alt text) are kept.
	if (/^h[1-6]$/.test(Component)) {
		const hasText =
			__html
				.replace(/<[^>]*>/g, "")
				.replace(/&nbsp;/gi, " ")
				.trim().length > 0;
		const hasMedia = /<(img|svg|picture|video|iframe|source)\b/i.test(__html);
		if (!hasText && !hasMedia) return null;
	}

	const variantType = getTextVariantType(props.variant);

	return (
		<Component
			className={cx(
				"cosmos-text",
				"cosmos-rich-text",
				variantType,
				css({
					textStyle: props.variant || CosmosTextVariantEnum.paragraphMD,
					overflowWrap: "anywhere",
				}),
				styles,
				props.className || "",
			)}
			style={style}
			dangerouslySetInnerHTML={innerHtml}
		></Component>
	);
};
